import { useEffect } from 'react';
import Link from 'next/link';
import Button from '@components/common/button';
import Modal from '@components/common/modal';

import Icon from '@components/common/assets';
import { useRequiredItems } from '@repository/match';
import { JDModalType } from '@components/position/jd/apply/modals';
import MatchingRequirementVO from '@domain/vo/match/MatchingRequirementVO';
import { ProfileItemType } from '@domain/constant/profileRequirementType';
import Colors from '@domain/constant/colors';
import { ProfileInfo } from '@domain/constant/profile/ProfileOverlayType';
import { V2RouteUrl } from '@utils/routeUtil';

import classnames from 'classnames/bind';
import styles from './profileRequiredFieldsModal.module.scss';
const cx = classnames.bind(styles);

interface Props {
  visible: boolean;
  onClose: () => void;
  positionSn: number;
  modalState: JDModalType;
  setModalState: (modalState: JDModalType) => void;
}

const ProfileRequiredFieldsModal = ({ visible, onClose, positionSn, modalState, setModalState }: Props) => {
  const { data: requiredItems } = useRequiredItems(positionSn, { enabled: visible && !!positionSn });

  useEffect(() => {
    if (!!requiredItems?.isProfileComplete) {
      setModalState('EASY_APPLY');
    }
  }, [requiredItems]);

  if (modalState !== 'PROFILE_REQUIRED') {
    return null;
  }

  return (
    <Modal visible={visible}>
      <section className={cx('layer')}>
        <div className={cx('closeBtn')} onClick={onClose}>
          <Icon name="closeLight" width={32} height={32} />
        </div>

        <h2 className={cx('title')}>
          이 포지션에 지원하기 위해
          <br />
          필요한 프로필 항목을 확인해 주세요.
        </h2>

        <ul className={cx('description')}>
          <li className={cx('content')}>
            <Icon name="checkLight" width={24} height={24} />
            프로필이 상세할수록 서류 합격률이 높아져요.
          </li>
          <li className={cx('content')}>
            <Icon name="checkLight" width={24} height={24} />
            포지션마다 요구하는 프로필 항목이 다를 수 있어요.
          </li>
        </ul>

        <ProfileRequiredItems requiredItems={requiredItems} />

        <Button className={cx('button')} onClick={onClose}>
          확인
        </Button>
      </section>
    </Modal>
  );
};

export default ProfileRequiredFieldsModal;

interface ProfileRequiredItemProps {
  requiredItems?: MatchingRequirementVO;
}

const ProfileRequiredItems = ({ requiredItems }: ProfileRequiredItemProps) => {
  if (!requiredItems || requiredItems.isProfileComplete) return null;

  return (
    <section className={cx('requiredItem', { justifyStart: requiredItems.requiredItems.length > 4 })}>
      {requiredItems?.requiredItems?.map(({ type, title, description }) => (
        <Link
          href={{ pathname: V2RouteUrl.PROFILE, query: { name: getProfileQuery(type) } }}
          target="_blank"
          className={cx('card')}
          key={type}
        >
          <div className={cx('iconError')}>
            <Icon name="errorLight" width={20} height={20} fill={Colors.C_ERROR} />
          </div>
          {getIcon(type)}
          <h3 className={cx('cardTitle')}>{title}</h3>
          {description && <div className={cx('cardDescription')}>{description}</div>}
        </Link>
      ))}
    </section>
  );
};

const getIcon = (type: ProfileItemType) => {
  switch (type) {
    case 'BASIC':
      return <Icon name="heartLight" width={32} height={32} />;
    case 'EDUCATION':
      return <Icon name="universityLight" width={32} height={32} />;
    case 'CAREER':
      return <Icon name="workLight" width={32} height={32} />;
    case 'PROJECT':
      return <Icon name="projectLight" width={32} height={32} />;
    case 'KNOWLEDGE_AND_SKILL':
      return <Icon name="designLight" width={32} height={32} />;
    case 'PREFERENTIAL':
      return <Icon name="militaryLight" width={32} height={32} />;
    case 'FILE':
      return <Icon name="folderLight" width={32} height={32} />;
    default:
      return null;
  }
};

const getProfileQuery = (type: ProfileItemType) => {
  switch (type) {
    case 'BASIC':
      return ProfileInfo.BASIC_INFO;
    case 'EDUCATION':
      return ProfileInfo.EDU;
    case 'CAREER':
      return ProfileInfo.CAREER;
    case 'PROJECT':
      return ProfileInfo.PROJECT;
    case 'KNOWLEDGE_AND_SKILL':
      return ProfileInfo.SKILL;
    case 'PREFERENTIAL':
      return ProfileInfo.MILITARY_PREFERENCE;
    case 'FILE':
      return ProfileInfo.ATTACHED_FILE;
    default:
      return '';
  }
};
